import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { enterpriseApi, financeFoundationApi, serviceTaxonomyApi } from "@/services/api";
import { toast } from "sonner";
import {
  Building2, GitBranch, Users2, Landmark, ListTree, Layers, Map,
  Plus, RefreshCw, ChevronRight, CheckCircle2, Clock, Archive,
  AlertTriangle, Info, X, Edit3
} from "lucide-react";

// ── Status badge ─────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  ACTIVE:   "bg-emerald-50 text-emerald-700 border border-emerald-200",
  DRAFT:    "bg-amber-50  text-amber-700  border border-amber-200",
  INACTIVE: "bg-stone-100 text-stone-500  border border-stone-200",
  ARCHIVED: "bg-rose-50   text-rose-600   border border-rose-200",
  DEMO_CONFIGURATION: "bg-sky-50 text-sky-700 border border-sky-200",
  REFERENCE:          "bg-violet-50 text-violet-700 border border-violet-200",
};
function StatusPill({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[status] || STATUS_COLORS.INACTIVE}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

// ── Config status pill (for Legal Entities) ──────────────────────────────────
function ConfigPill({ status }) {
  if (!status || status === "VERIFIED") return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
      <AlertTriangle size={10} />
      {status === "DEMO_CONFIGURATION" ? "Demo Config" : "Reference"}
    </span>
  );
}

// ── Simple entity row ─────────────────────────────────────────────────────────
function EntityRow({ entity, onEdit }) {
  return (
    <div
      data-testid={`entity-row-${entity.id}`}
      className="flex items-center justify-between px-4 py-3 border-b border-stone-100 hover:bg-stone-50 transition-colors group"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-xs font-mono text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded shrink-0">
          {entity.code}
        </span>
        <span className="text-sm font-medium text-stone-800 truncate">{entity.name || entity.legal_name}</span>
        <ConfigPill status={entity.configuration_status} />
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <StatusPill status={entity.status} />
        {onEdit && (
          <button
            onClick={() => onEdit(entity)}
            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-stone-200 transition-all"
          >
            <Edit3 size={13} className="text-stone-500" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Card wrapper ─────────────────────────────────────────────────────────────
function EntityCard({ title, icon: Icon, count, children, onAdd, addLabel = "Add", loading }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100 bg-stone-50">
        <div className="flex items-center gap-2">
          <Icon size={15} className="text-stone-500" />
          <span className="text-sm font-semibold text-stone-700">{title}</span>
          {count !== undefined && (
            <span className="text-xs text-stone-400 bg-stone-200 px-1.5 rounded">{count}</span>
          )}
        </div>
        {onAdd && (
          <button
            onClick={onAdd}
            data-testid={`add-${title.toLowerCase().replace(/\s+/g, "-")}`}
            className="flex items-center gap-1 text-xs text-stone-600 bg-white border border-stone-200 px-2.5 py-1 rounded-lg hover:border-stone-400 hover:bg-stone-50 transition-all"
          >
            <Plus size={12} /> {addLabel}
          </button>
        )}
      </div>
      <div className="divide-y divide-stone-50">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-stone-400 text-sm">
            <RefreshCw size={14} className="animate-spin mr-2" /> Loading…
          </div>
        ) : count === 0 ? (
          <div className="text-center py-8 text-stone-400 text-sm">No records yet</div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

// ── Axis section label ────────────────────────────────────────────────────────
function AxisLabel({ title, subtitle }) {
  return (
    <div className="mb-4">
      <h3 className="text-base font-semibold text-stone-800">{title}</h3>
      {subtitle && <p className="text-xs text-stone-500 mt-0.5">{subtitle}</p>}
    </div>
  );
}

// ── Mapping row ──────────────────────────────────────────────────────────────
const ENTITY_TYPE_LABELS = {
  LEGAL_ENTITY: "Legal Entity",
  DIVISION: "Division",
  BUSINESS_UNIT: "Business Unit",
  PROFIT_CENTER: "Profit Center",
  DEPARTMENT: "Department",
  COST_CENTER: "Cost Center",
  LEDGER: "Ledger",
  COA: "Chart of Accounts",
};

function MappingRow({ mapping, entityLookup }) {
  const isExpired = mapping.effective_to && mapping.effective_to < new Date().toISOString().slice(0, 10);

  const resolve = (id, typeHint) => {
    const entry = entityLookup?.[id];
    if (entry) return entry;
    return { code: null, name: null, type: typeHint };
  };

  const src = resolve(mapping.source_entity_id, mapping.source_entity_type);
  const tgt = resolve(mapping.target_entity_id, mapping.target_entity_type);

  const displayEntity = (ent) => {
    if (ent.name) return ent.code ? `${ent.name} · ${ent.code}` : ent.name;
    return ent.type ? ENTITY_TYPE_LABELS[ent.type] || ent.type.replace(/_/g, " ") : "—";
  };

  return (
    <div className="flex items-start gap-3 px-4 py-3 border-b border-stone-100 hover:bg-stone-50 text-sm">
      <span className="text-xs font-mono bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded shrink-0 mt-0.5">
        {mapping.mapping_type?.replace(/_/g, " ")}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="min-w-0">
            <p className="text-[10px] text-stone-400 leading-none mb-0.5">
              {ENTITY_TYPE_LABELS[src.type] || src.type?.replace(/_/g, " ")}
            </p>
            <p className="text-stone-700 font-medium text-sm truncate">{displayEntity(src)}</p>
          </div>
          <ChevronRight size={12} className="text-stone-400 shrink-0 mt-3" />
          <div className="min-w-0">
            <p className="text-[10px] text-stone-400 leading-none mb-0.5">
              {ENTITY_TYPE_LABELS[tgt.type] || tgt.type?.replace(/_/g, " ")}
            </p>
            <p className="text-stone-700 text-sm truncate">{displayEntity(tgt)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-1.5 text-xs text-stone-400">
          <span>From: {mapping.effective_from}</span>
          {mapping.effective_to && <span>To: {mapping.effective_to}</span>}
          {isExpired && <span className="text-rose-500 font-medium">Expired</span>}
        </div>
      </div>
      <StatusPill status={mapping.status} />
    </div>
  );
}

// ── COA Segment row ──────────────────────────────────────────────────────────
function SegmentRow({ seg, idx }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-stone-100 text-sm">
      <span className="text-xs text-stone-400 w-6 text-center font-mono">{seg.sequence}</span>
      <span className="text-xs font-mono bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded shrink-0">
        {seg.segment_code}
      </span>
      <span className="text-stone-700 flex-1">{seg.segment_name}</span>
      <span className="text-xs text-stone-400">{seg.segment_type}</span>
      {!seg.required && <span className="text-xs text-stone-400 italic">Optional</span>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TABS
// ══════════════════════════════════════════════════════════════════════════════

const TABS = [
  { id: "legal",     label: "Legal Structure",    icon: Landmark },
  { id: "business",  label: "Business Structure",  icon: Building2 },
  { id: "functional",label: "Functional Structure",icon: Users2 },
  { id: "financial", label: "Financial Structure", icon: Layers },
  { id: "mappings",  label: "Mappings",            icon: Map },
  { id: "taxonomy",  label: "Service Taxonomy",    icon: ListTree },
];

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
export default function EnterpriseAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("legal");

  // Data state
  const [legal, setLegal] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [bus, setBus] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const [depts, setDepts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [entityLookup, setEntityLookup] = useState({});
  const [ledgers, setLedgers] = useState([]);
  const [coa, setCoa] = useState([]);
  const [coaVersions, setCoaVersions] = useState([]);
  const [segments, setSegments] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [taxonomy, setTaxonomy] = useState([]);

  const [loadingTab, setLoadingTab] = useState(null);
  const [activeCoaId, setActiveCoaId] = useState(null);
  const [activeVersionId, setActiveVersionId] = useState(null);

  const loadTab = useCallback(async (t) => {
    setLoadingTab(t);
    try {
      if (t === "legal") {
        const r = await enterpriseApi.listLegalEntities();
        setLegal(r.data || []);
      } else if (t === "business") {
        const [d, b, pc] = await Promise.all([
          enterpriseApi.listDivisions(),
          enterpriseApi.listBusinessUnits(),
          enterpriseApi.listProfitCenters(),
        ]);
        setDivisions(d.data || []);
        setBus(b.data || []);
        setProfitCenters(pc.data || []);
      } else if (t === "functional") {
        const [dts, ccs] = await Promise.all([
          enterpriseApi.listDepartments(),
          enterpriseApi.listCostCenters(),
        ]);
        setDepts(dts.data || []);
        setCostCenters(ccs.data || []);
      } else if (t === "financial") {
        const [l, c, cal, cur] = await Promise.all([
          financeFoundationApi.listLedgers(),
          financeFoundationApi.listCOA(),
          financeFoundationApi.listCalendars(),
          financeFoundationApi.listCurrencies(),
        ]);
        setLedgers(l.data || []);
        const coaList = c.data || [];
        setCoa(coaList);
        setCalendars(cal.data || []);
        setCurrencies(cur.data || []);
        // Load versions for first COA
        if (coaList[0]) {
          setActiveCoaId(coaList[0].id);
          const vRes = await financeFoundationApi.listCOAVersions(coaList[0].id);
          const versions = vRes.data || [];
          setCoaVersions(versions);
          const activeVer = versions.find(v => v.status === "ACTIVE") || versions[0];
          if (activeVer) {
            setActiveVersionId(activeVer.id);
            const sRes = await financeFoundationApi.listSegments(activeVer.id);
            setSegments(sRes.data || []);
          }
        }
      } else if (t === "mappings") {
        const [mR, leR, divR, buR, pcR, deptR, ccR, ledgR] = await Promise.all([
          enterpriseApi.listMappings({}),
          enterpriseApi.listLegalEntities(),
          enterpriseApi.listDivisions(),
          enterpriseApi.listBusinessUnits(),
          enterpriseApi.listProfitCenters(),
          enterpriseApi.listDepartments(),
          enterpriseApi.listCostCenters(),
          financeFoundationApi.listLedgers(),
        ]);
        setMappings(mR.data || []);

        // Build flat entity lookup: { [id]: { code, name, type } }
        const lookup = {};
        for (const le of (leR.data || []))   lookup[le.id] = { code: le.code, name: le.legal_name, type: "LEGAL_ENTITY" };
        for (const div of (divR.data || []))  lookup[div.id] = { code: div.code, name: div.name, type: "DIVISION" };
        for (const bu of (buR.data || []))    lookup[bu.id] = { code: bu.code, name: bu.name, type: "BUSINESS_UNIT" };
        for (const pc of (pcR.data || []))    lookup[pc.id] = { code: pc.code, name: pc.name, type: "PROFIT_CENTER" };
        for (const d of (deptR.data || []))   lookup[d.id] = { code: d.code, name: d.name, type: "DEPARTMENT" };
        for (const cc of (ccR.data || []))    lookup[cc.id] = { code: cc.code, name: cc.name, type: "COST_CENTER" };
        for (const l of (ledgR.data || []))   lookup[l.id] = { code: l.code, name: l.name, type: "LEDGER" };
        setEntityLookup(lookup);
      } else if (t === "taxonomy") {
        const r = await serviceTaxonomyApi.listCategories({});
        setTaxonomy(r.data || []);
      }
    } catch (e) {
      toast.error(`Could not load ${t} data`);
    } finally {
      setLoadingTab(null);
    }
  }, []);

  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  const tabLoading = loadingTab === tab;

  return (
    <div className="min-h-screen bg-[#faf9f7]" data-testid="enterprise-admin-page">
      {/* Page header */}
      <div className="bg-white border-b border-stone-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-stone-900">Enterprise Structure</h1>
            <p className="text-xs text-stone-500 mt-0.5">
              UEIM-00 — Canonical enterprise, financial context, and service taxonomy foundation
            </p>
          </div>
          <button
            onClick={() => loadTab(tab)}
            className="flex items-center gap-1.5 text-xs text-stone-500 border border-stone-200 px-3 py-1.5 rounded-lg hover:border-stone-400 transition-all"
          >
            <RefreshCw size={12} className={tabLoading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mt-4 overflow-x-auto pb-px">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`enterprise-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                tab === id
                  ? "bg-stone-900 text-white"
                  : "text-stone-500 hover:text-stone-700 hover:bg-stone-100"
              }`}
            >
              <Icon size={13} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 max-w-7xl mx-auto">

        {/* ── LEGAL STRUCTURE ─────────────────────────────────────── */}
        {tab === "legal" && (
          <div className="space-y-6">
            <AxisLabel
              title="Legal Structure"
              subtitle="Statutory legal entities and their tax registrations. All legal entities are segregated by tenant."
            />
            <EntityCard
              title="Legal Entities"
              icon={Landmark}
              count={legal.length}
              loading={tabLoading}
              addLabel="Add Legal Entity"
            >
              {legal.map(le => <EntityRow key={le.id} entity={{ ...le, name: le.legal_name }} />)}
            </EntityCard>
          </div>
        )}

        {/* ── BUSINESS STRUCTURE ──────────────────────────────────── */}
        {tab === "business" && (
          <div className="space-y-6">
            <AxisLabel
              title="Business Structure"
              subtitle="Divisions, Business Units, and Profit Centers. Independent of the functional axis — connected via explicit mappings."
            />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <EntityCard title="Divisions" icon={GitBranch} count={divisions.length} loading={tabLoading}>
                {divisions.map(d => <EntityRow key={d.id} entity={d} />)}
              </EntityCard>
              <EntityCard title="Business Units" icon={Building2} count={bus.length} loading={tabLoading}>
                {bus.map(b => (
                  <div key={b.id} className="px-4 py-3 border-b border-stone-100 hover:bg-stone-50">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded">{b.code}</span>
                      <StatusPill status={b.status} />
                    </div>
                    <p className="text-sm font-medium text-stone-800 mt-1">{b.name}</p>
                    {b.city && <p className="text-xs text-stone-400 mt-0.5">{b.city}, {b.state}</p>}
                    {b.default_currency && (
                      <span className="text-xs text-stone-400">Currency: {b.default_currency}</span>
                    )}
                  </div>
                ))}
              </EntityCard>
              <EntityCard title="Profit Centers" icon={Layers} count={profitCenters.length} loading={tabLoading}>
                {profitCenters.map(p => <EntityRow key={p.id} entity={p} />)}
              </EntityCard>
            </div>
          </div>
        )}

        {/* ── FUNCTIONAL STRUCTURE ────────────────────────────────── */}
        {tab === "functional" && (
          <div className="space-y-6">
            <AxisLabel
              title="Functional Structure"
              subtitle="Departments and Cost Centers. A department may map to one or more cost centers via enterprise mappings."
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <EntityCard title="Departments" icon={Users2} count={depts.length} loading={tabLoading}>
                {depts.map(d => (
                  <div key={d.id} className="flex items-center justify-between px-4 py-3 border-b border-stone-100 hover:bg-stone-50">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded">{d.code}</span>
                      <span className="text-sm text-stone-800">{d.name}</span>
                    </div>
                    <StatusPill status={d.status} />
                  </div>
                ))}
              </EntityCard>
              <EntityCard title="Cost Centers" icon={Layers} count={costCenters.length} loading={tabLoading}>
                {costCenters.map(cc => <EntityRow key={cc.id} entity={cc} />)}
              </EntityCard>
            </div>
          </div>
        )}

        {/* ── FINANCIAL STRUCTURE ─────────────────────────────────── */}
        {tab === "financial" && (
          <div className="space-y-6">
            <AxisLabel
              title="Financial Structure"
              subtitle="Ledgers, Chart of Accounts (COA), Accounting Calendar, and Currencies. COA versioning ensures historical integrity."
            />

            {/* Ledgers + Currencies side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <EntityCard title="Ledgers" icon={Landmark} count={ledgers.length} loading={tabLoading}>
                {ledgers.map(l => (
                  <div key={l.id} className="px-4 py-3 border-b border-stone-100 hover:bg-stone-50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-mono text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded">{l.code}</span>
                      <StatusPill status={l.status} />
                    </div>
                    <p className="text-sm font-medium text-stone-800">{l.name}</p>
                    <p className="text-xs text-stone-400 mt-0.5">
                      {l.currency_code} · {l.accounting_method}
                    </p>
                  </div>
                ))}
              </EntityCard>
              <EntityCard title="Currencies" icon={Layers} count={currencies.length} loading={tabLoading}>
                {currencies.map(c => (
                  <div key={c.id || c.currency_code} className="flex items-center justify-between px-4 py-2.5 border-b border-stone-100 hover:bg-stone-50">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono font-medium text-stone-600">{c.currency_code}</span>
                      <span className="text-sm text-stone-700">{c.name}</span>
                    </div>
                    <span className="text-sm text-stone-400">{c.symbol}</span>
                  </div>
                ))}
              </EntityCard>
            </div>

            {/* Accounting Calendar */}
            <EntityCard title="Accounting Calendars" icon={Clock} count={calendars.length} loading={tabLoading}>
              {calendars.map(cal => (
                <div key={cal.id} className="px-4 py-3 border-b border-stone-100 hover:bg-stone-50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-stone-800">{cal.name}</span>
                    <StatusPill status={cal.status} />
                  </div>
                  <div className="flex items-center gap-3 text-xs text-stone-400">
                    <span>Type: {cal.calendar_type}</span>
                    <span>FY Start Month: {cal.fiscal_year_start_month}</span>
                    <span>{(cal.periods || []).length} periods</span>
                  </div>
                </div>
              ))}
            </EntityCard>

            {/* Chart of Accounts */}
            {coa.length > 0 && (
              <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100 bg-stone-50">
                  <div className="flex items-center gap-2">
                    <ListTree size={15} className="text-stone-500" />
                    <span className="text-sm font-semibold text-stone-700">Chart of Accounts</span>
                    <span className="text-xs text-stone-400 bg-stone-200 px-1.5 rounded">{coa.length}</span>
                  </div>
                </div>
                {coa.map(c => (
                  <div key={c.id} className="px-4 py-3 border-b border-stone-100">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="text-xs font-mono text-stone-400 mr-2">{c.code}</span>
                        <span className="text-sm font-medium text-stone-800">{c.name}</span>
                      </div>
                      <StatusPill status={c.status} />
                    </div>
                    {/* Versions */}
                    {coaVersions.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-stone-500 mb-1.5 font-medium">Versions:</p>
                        <div className="flex gap-2 flex-wrap">
                          {coaVersions.map(v => (
                            <button
                              key={v.id}
                              onClick={() => setActiveVersionId(v.id)}
                              className={`text-xs px-2.5 py-1 rounded-lg border transition-all ${
                                activeVersionId === v.id
                                  ? "bg-stone-900 text-white border-stone-900"
                                  : "text-stone-600 border-stone-200 hover:border-stone-400"
                              }`}
                            >
                              v{v.version_number} ({v.status})
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* COA Segments for active version */}
                {segments.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-stone-50 border-b border-stone-100">
                      <p className="text-xs font-medium text-stone-600">
                        Segments — {coaVersions.find(v => v.id === activeVersionId)?.status === "ACTIVE" ? "Active version" : "Selected version"}
                      </p>
                    </div>
                    {segments.map((s, i) => <SegmentRow key={s.id} seg={s} idx={i} />)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── MAPPINGS ──────────────────────────────────────────────── */}
        {tab === "mappings" && (
          <div className="space-y-6">
            <AxisLabel
              title="Enterprise Mappings"
              subtitle="Effective-dated cross-axis relationships. The Accounting Context Resolver uses these to derive full financial context from a transaction date."
            />
            <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100 bg-stone-50">
                <div className="flex items-center gap-2">
                  <Map size={15} className="text-stone-500" />
                  <span className="text-sm font-semibold text-stone-700">All Mappings</span>
                  <span className="text-xs text-stone-400 bg-stone-200 px-1.5 rounded">{mappings.length}</span>
                </div>
              </div>
              {tabLoading ? (
                <div className="flex items-center justify-center py-8 text-stone-400 text-sm">
                  <RefreshCw size={14} className="animate-spin mr-2" /> Loading…
                </div>
              ) : (
                <div>
                  {/* Group by mapping type */}
                  {Array.from(new Set(mappings.map(m => m.mapping_type))).sort().map(type => (
                    <div key={type}>
                      <div className="px-4 py-2 bg-stone-50 border-y border-stone-100">
                        <span className="text-xs font-semibold text-stone-600">{type?.replace(/_/g, " ")}</span>
                      </div>
                      {mappings.filter(m => m.mapping_type === type).map(m => (
                        <MappingRow key={m.id} mapping={m} entityLookup={entityLookup} />
                      ))}
                    </div>
                  ))}
                  {mappings.length === 0 && (
                    <div className="text-center py-8 text-stone-400 text-sm">No mappings found</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── SERVICE TAXONOMY ───────────────────────────────────────── */}
        {tab === "taxonomy" && (
          <div className="space-y-6">
            <AxisLabel
              title="Service Taxonomy"
              subtitle="Canonical event service categories. Shared across CRM, Moodboards, Sourcing, Costing, Proposals, and Finance. Never create module-specific category lists."
            />
            <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100 bg-stone-50">
                <div className="flex items-center gap-2">
                  <ListTree size={15} className="text-stone-500" />
                  <span className="text-sm font-semibold text-stone-700">Event Service Categories</span>
                  <span className="text-xs text-stone-400 bg-stone-200 px-1.5 rounded">{taxonomy.length}</span>
                </div>
              </div>
              {tabLoading ? (
                <div className="flex items-center justify-center py-8 text-stone-400 text-sm">
                  <RefreshCw size={14} className="animate-spin mr-2" /> Loading…
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {taxonomy.map(sc => (
                    <div
                      key={sc.id}
                      data-testid={`service-category-${sc.code}`}
                      className="flex items-center justify-between px-4 py-3 border-b border-r border-stone-100 hover:bg-stone-50 transition-colors"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-mono text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded shrink-0">
                          {sc.code?.replace("SVC-", "")}
                        </span>
                        <span className="text-sm text-stone-800 truncate">{sc.name}</span>
                      </div>
                      <StatusPill status={sc.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
